/**
 * Reading a tracked hand: is it pinching, and what did it mean to pick up.
 *
 * WebXR hand tracking hands us joint positions and nothing else. Everything a
 * lesson cares about -- holding, letting go, which bone -- is arithmetic on
 * those, so it lives here where it can be tested rather than in the renderer
 * where it cannot (ADR-025).
 */

export type Point = readonly [number, number, number];

/**
 * Fingertip and thumb this close means a pinch has begun.
 *
 * Roughly the width of a child's fingertip. Tighter and a small hand cannot
 * close it; looser and a relaxed hand grabs things it passes.
 */
export const PINCH_CLOSE = 0.022;

/**
 * And this far apart means it has ended.
 *
 * Deliberately larger than `PINCH_CLOSE`. Tracking jitters by a few
 * millimetres, and with a single threshold a held pinch flickers on and off
 * at the boundary -- which in a lesson means the bone a child is carrying
 * drops itself halfway across the room. Closing is decided tightly; letting
 * go has to be meant.
 */
export const PINCH_OPEN = 0.04;

/**
 * How far from a hand a thing can be and still be picked up.
 *
 * Near-field, not a laser: the point of hands is reaching out and taking
 * hold of something, and a child who can grab a bone from across the room
 * has learned nothing about where it is.
 */
export const GRAB_REACH_METRES = 0.09;

function distance(a: Point, b: Point): number {
  return Math.hypot(a[0] - b[0], a[1] - b[1], a[2] - b[2]);
}

/**
 * Whether a hand is pinching, given where it was a moment ago.
 *
 * `wasPinching` is what makes the two thresholds work, so callers have to
 * keep it. A caller that always passes false gets a twitchy grip.
 */
export function isPinching(thumbTip: Point, indexTip: Point, wasPinching: boolean): boolean {
  const gap = distance(thumbTip, indexTip);
  return wasPinching ? gap <= PINCH_OPEN : gap <= PINCH_CLOSE;
}

/**
 * The nearest thing a hand could have meant, or nothing if it is empty-handed.
 *
 * Nearest wins, because it is the only rule a child finds predictable when
 * two bones are in the same handful.
 */
export function nearestWithin<T extends { at: Point }>(
  hand: Point,
  candidates: readonly T[],
  reach = GRAB_REACH_METRES,
): T | undefined {
  let best: T | undefined;
  let bestDistance = reach;

  for (const candidate of candidates) {
    const away = distance(hand, candidate.at);
    if (away <= bestDistance) {
      bestDistance = away;
      best = candidate;
    }
  }
  return best;
}
